// The base map style: what the map is built on before any weather is added.
//
// Almost all of this is a pure function of the basemap capabilities — given
// { vector, dem } it returns a StyleSpecification, with no map instance and no
// app state involved. That is why it comes out of app.ts cleanly, and it is
// worth keeping that way: a style is data, and the moment it starts reading
// mutable state it stops being inspectable.
//
// The one exception is detectBasemap, which asks the server what this clone
// actually has on disk. Everything the answer changes is expressed in the style
// it produces, not in a flag other modules read.

import { EMPTY_FC } from '../state.ts';
import { mapTheme } from './theme.ts';
import type { ExpressionSpecification, LayerSpecification, StyleSpecification } from 'maplibre-gl';

// Which optional basemap artifacts this clone actually has on disk.
export interface BasemapCaps {
  vector: boolean;
  dem: boolean;
}

// The self-hosted basemap is ~830MB of generated data, so it is optional the same way
// the weather is: a clone without it still runs, just without terrain and roads. Ask
// the server once rather than letting MapLibre discover it by erroring on every tile.
export async function detectBasemap(): Promise<BasemapCaps> {
  const ok = async (url: string): Promise<boolean> => {
    try {
      const r = await fetch(url, { method: 'HEAD' });
      return r.ok || r.status === 204;
    } catch {
      return false;
    }
  };
  const [vector, dem] = await Promise.all([ok('/basemap/0/0/0.mvt'), ok('/dem/0/0/0.png')]);
  if (!vector) console.info('No self-hosted basemap — run npm run build:tiles for terrain and roads.');
  return { vector, dem };
}

// Detail rides in from here: below it the field is the whole point and nothing should
// compete, above it the weather stops gaining detail (the grid tops out at zoom 5) and
// the ground under it should start carrying the eye instead.
export const DETAIL_ZOOM = 5;
// Everything fades across the zoom either side of it rather than popping into view.
const fadeIn = (to: number): ExpressionSpecification => ['interpolate', ['linear'], ['zoom'], DETAIL_ZOOM - 0.5, 0, DETAIL_ZOOM + 0.5, to];

export function baseStyle(caps: BasemapCaps = { vector: false, dem: false }): StyleSpecification {
  // The ground's colours follow the system scheme (map/theme.ts); nothing that
  // colours data is themed.
  const T = mapTheme();
  return {
    version: 8,
    // Self-hosted SDF glyphs for the city labels — MapLibre cannot draw text
    // without them, and silently renders nothing if this 404s. Only the 0-255
    // range is vendored: labels use each city's ASCII name, which the whole
    // filtered set has, so no other range is ever requested.
    glyphs: '/assets/glyphs/{fontstack}/{range}.pbf',
    sources: {
      // Natural Earth 50m. Both the land and the mask cut from it start empty and
      // are filled by loadLand: the geometry has to be parsed here anyway to build
      // the mask, so handing MapLibre a URL as well would fetch and parse it twice.
      land: { type: 'geojson', data: EMPTY_FC },
      // tolerance 0: do not simplify the mask. Every hole in it is a landmass, and a
      // hole that simplification degrades — or collapses below the 4 points a ring
      // needs and drops outright — stops cutting, so the sea paints straight over a
      // continent. That is what ate the Americas at zoom 2.5. The land layer keeps
      // the default: it is ordinary polygons, where simplification only costs detail.
      'ocean-mask': { type: 'geojson', data: EMPTY_FC, tolerance: 0 },
      'mask-islands': { type: 'geojson', data: EMPTY_FC },
      boundaries: { type: 'geojson', data: '/assets/boundaries.geojson.gz' },
      'urban-areas': { type: 'geojson', data: '/assets/urban-areas.geojson.gz' },
      'country-labels': { type: 'geojson', data: '/assets/country-labels.geojson.gz' },
      weather: { type: 'geojson', data: EMPTY_FC },
      // The wind arrow lattice for the current viewport — see refreshWind.
      wind: { type: 'geojson', data: EMPTY_FC },
      // OpenStreetMap, extracted to z8 and served from data/ — see build-tiles.ts.
      ...(caps.vector
        ? { basemap: { type: 'vector', tiles: [`${location.origin}/basemap/{z}/{x}/{y}.mvt`], minzoom: 0, maxzoom: 8 } }
        : {}),
      // Elevation only goes to z6 and MapLibre overzooms it past that. Relief is
      // smooth, so a soft wash is all that is lost — and z0-8 elevation would be 5GB
      // against 310MB for z0-6.
      ...(caps.dem
        ? {
            dem: {
              type: 'raster-dem',
              tiles: [`${location.origin}/dem/{z}/{x}/{y}.png`],
              encoding: 'terrarium',
              tileSize: 256,
              minzoom: 0,
              maxzoom: 6,
            },
          }
        : {}),
    },
    layers: [
      { id: 'ocean', type: 'background', paint: { 'background-color': T.ocean } },
      {
        id: 'land',
        type: 'fill',
        source: 'land',
        paint: { 'fill-color': T.land, 'fill-opacity': T.landOpacity },
      },
      ...(caps.dem ? [hillshadeLayer()] : []),
      urbanLayer(),
      ...(caps.vector ? landuseLayers() : []),
      // Field frames are inserted here — above the ground, below the water (see
      // fieldBeforeId).
      ...(caps.vector ? [waterLayer()] : []),
      {
        id: 'ocean-mask-fill',
        type: 'fill',
        source: 'ocean-mask',
        paint: { 'fill-color': T.ocean, 'fill-antialias': false },
      },
      // The islands the mask had to give up, painted back over it so they read as
      // land rather than sea. Land tint only — the field above is already hidden.
      {
        id: 'mask-islands-fill',
        type: 'fill',
        source: 'mask-islands',
        paint: { 'fill-color': T.land, 'fill-opacity': T.landOpacity },
      },
      // Roads go above the mask, not below it: they are the one detail layer that
      // should never be clipped away by water it happens to run along.
      ...(caps.vector ? roadLayers() : []),
      {
        id: 'land-outline',
        type: 'line',
        source: 'land',
        paint: { 'line-color': T.coast, 'line-width': 0.8, 'line-opacity': T.coastOpacity },
      },
    ],
  };
}

// Terrain. There is no hillshade-opacity, so exaggeration carries the fade: flat below
// DETAIL_ZOOM, modelled above it.
//
// It sits under the field, which means the 0.6 wash leaves it 40% of the pixel — so
// what looks violent on its own is what reads as "slightly visible" through weather.
// That is also why it belongs down here rather than on top: relief this strong painted
// over the field would tint the colours the whole map exists to show. Turn the field
// off and this is a relief map; leave it on and it is texture under the weather.
const hillshadeLayer = (): LayerSpecification => ({
  id: 'hillshade',
  type: 'hillshade',
  source: 'dem',
  // Not just cosmetic: a layer's zoom range is also what decides whether its source
  // fetches anything. Without this the world view quietly pulled ~3MB of elevation to
  // render at zero exaggeration.
  minzoom: DETAIL_ZOOM - 1,
  paint: {
    'hillshade-exaggeration': fadeIn(1),
    'hillshade-shadow-color': mapTheme().hillShadow,
    'hillshade-highlight-color': mapTheme().hillHighlight,
    'hillshade-accent-color': mapTheme().ocean,
  },
});

// Urban extent — the shape of a city, as against the dot that marks it. This is
// Natural Earth's generalisation, and it exists because OSM's own landuse has nothing
// to say at the zooms where the question is asked: 3 residential polygons across Europe
// at zoom 5, 12 at zoom 6, and it only wakes up at 7. So this carries zoom 4-7 and
// hands over to the real thing as it arrives, rather than both drawing a coarse blob
// and a precise outline over each other.
//
// Ships with the Natural Earth assets rather than the 830MB extract, so it works
// whether or not the self-hosted basemap was ever built.
const urbanLayer = (): LayerSpecification => ({
  id: 'urban-areas',
  type: 'fill',
  source: 'urban-areas',
  minzoom: DETAIL_ZOOM - 1,
  paint: {
    // Nearly opaque, for the same reason the hillshade is at full exaggeration: this
    // sits under the field, so 0.6 of every pixel is weather and what is left has to
    // carry on its own. Cool against the warm end of every palette, so a city reads as
    // a city and not as a cold spot.
    'fill-color': mapTheme().urban,
    'fill-opacity': ['interpolate', ['linear'], ['zoom'], DETAIL_ZOOM - 0.5, 0, DETAIL_ZOOM + 0.5, 0.9, 7, 0.9, 7.8, 0],
    'fill-antialias': false,
  },
});

// Ground cover, in two groups rather than twenty colours. The field above is already
// carrying every hue there is, so these only shift the land tint a little lighter
// where people are and a little darker where they are not — enough to read as
// settlement and countryside, not enough to argue with the weather.
function landuseLayers(): LayerSpecification[] {
  const BUILT = ['residential', 'commercial', 'industrial', 'military', 'railway', 'aerodrome', 'university', 'hospital'];
  const GREEN = ['forest', 'wood', 'park', 'nature_reserve', 'protected_area', 'national_park', 'grassland', 'grass', 'meadow', 'farmland', 'scrub', 'wetland', 'golf_course', 'cemetery', 'zoo'];
  return [
    {
      id: 'landuse-green',
      type: 'fill',
      source: 'basemap',
      'source-layer': 'landuse',
      minzoom: DETAIL_ZOOM - 1,
      filter: ['match', ['get', 'kind'], GREEN, true, false],
      paint: { 'fill-color': mapTheme().green, 'fill-opacity': fadeIn(0.5), 'fill-antialias': false },
    },
    {
      id: 'landuse-built',
      type: 'fill',
      source: 'basemap',
      'source-layer': 'landuse',
      // Takes over from urban-areas as it fades out at 7: below that OSM has almost
      // nothing here, above it OSM has the truth and the generalisation is in the way.
      minzoom: 6,
      filter: ['match', ['get', 'kind'], BUILT, true, false],
      paint: {
        'fill-color': mapTheme().urban, // same tint the generalisation used, so the handover does not show
        'fill-opacity': ['interpolate', ['linear'], ['zoom'], 6.8, 0, 7.8, 0.9],
        'fill-antialias': false,
      },
    },
  ];
}

// Inland water. Lakes and rivers arrive here from OSM rather than from the ocean mask,
// and painting them above the field is what stops a lake reading as ground.
//
// Not at full strength, though. The sea is a big enough shape to read as sea in solid
// black, but a lake is a small dark hole punched in a bright field and a river is a
// black thread drawn across it — at the same weight they pull the eye far harder than
// their size deserves. Letting the field through leaves them unmistakably water while
// putting them back in the background.
const waterLayer = (): LayerSpecification => ({
  id: 'basemap-water',
  type: 'fill',
  source: 'basemap',
  'source-layer': 'water',
  minzoom: DETAIL_ZOOM - 1,
  paint: { 'fill-color': mapTheme().ocean, 'fill-opacity': fadeIn(0.45), 'fill-antialias': false },
});

// Roads. The extract stops at z8, and OSM only promotes minor roads below that, so
// what survives is motorways and trunk roads — which is exactly the density this map
// wants: enough to place a city, never enough to become the subject.
function roadLayers(): LayerSpecification[] {
  return [
    {
      id: 'roads-major',
      type: 'line',
      source: 'basemap',
      'source-layer': 'roads',
      minzoom: DETAIL_ZOOM - 1,
      filter: ['==', ['get', 'kind'], 'major_road'],
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: {
        'line-color': mapTheme().roadMajor,
        'line-opacity': fadeIn(0.3),
        'line-width': ['interpolate', ['linear'], ['zoom'], DETAIL_ZOOM, 0.3, 8, 1.0],
      },
    },
    {
      id: 'roads-highway',
      type: 'line',
      source: 'basemap',
      'source-layer': 'roads',
      minzoom: DETAIL_ZOOM - 1,
      filter: ['==', ['get', 'kind'], 'highway'],
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: {
        'line-color': mapTheme().roadHighway,
        'line-opacity': fadeIn(0.45),
        'line-width': ['interpolate', ['linear'], ['zoom'], DETAIL_ZOOM, 0.4, 8, 1.6],
      },
    },
  ];
}

export const LAND_URL = '/assets/land-50m.geojson.gz';

// Where to open the map so it lands on the viewer's part of the world rather than a
// bare full globe. Longitude comes from the timezone offset (15°/hour — the user's
// slice, centred); latitude from a coarse per-region guess off the IANA zone name.
// The zoom (~2.5) shows roughly half the globe. All best-effort with a safe default.
